<template>
  <div class="edit-tickets-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Edit tickets</h5>
    </div>

    <div class="view-body">
      <div v-if="loadingCategories" class="text-center py-4">
        <i class="el-icon-loading" style="font-size: 20px; color: rgba(125,125,125,0.7);"></i>
      </div>

      <template v-else>
        <!-- Category rows -->
        <div
          v-for="(cat, i) in categories"
          :key="'cat' + i"
          class="pricing-row"
        >
          <div class="pricing-row__info">
            <div class="pricing-row__name-line">
              <span class="pricing-row__name text-white">{{ cat.category }}</span>
              <template v-if="!cat.editing">
                <el-tooltip content="Override price" placement="top" :open-delay="300">
                  <i class="tim-icons icon-pencil pricing-row__edit-icon" @click="startEditPrice(cat)"></i>
                </el-tooltip>
              </template>
            </div>
            <template v-if="!cat.editing">
              <span class="pricing-row__rate">{{ getRate(cat) }}</span>
              <span v-if="cat.original_price !== undefined && cat.price !== cat.original_price" class="pricing-row__was">
                overridden from {{ getAmount(cat.original_price) }}
                <el-tooltip content="Restore original price" placement="top" :open-delay="300">
                  <i class="tim-icons icon-refresh-02 pricing-row__reset-icon" @click="restoreOriginalPrice(cat)"></i>
                </el-tooltip>
              </span>
            </template>
            <template v-else>
              <div class="pricing-row__inline-edit">
                <base-input class="mb-0 pricing-row__price-input">
                  <el-input-number
                    v-model="cat.price"
                    :min="0"
                    :precision="2"
                    :step="0.01"
                    :controls="false"
                    size="mini"
                  />
                </base-input>
                <el-tooltip content="Save" placement="top" :open-delay="300">
                  <i class="tim-icons icon-check-2 pricing-row__save-icon" @click="saveEditPrice(cat)"></i>
                </el-tooltip>
                <el-tooltip content="Restore original price" placement="top" :open-delay="300">
                  <i class="tim-icons icon-refresh-02 pricing-row__restore-icon" @click="restoreOriginalPrice(cat)"></i>
                </el-tooltip>
                <el-tooltip content="Cancel" placement="top" :open-delay="300">
                  <i class="tim-icons icon-simple-remove pricing-row__cancel-icon" @click="cancelEditPrice(cat)"></i>
                </el-tooltip>
              </div>
            </template>
          </div>
          <div class="pricing-row__qty">
            <base-input class="mb-0" label="Qty">
              <el-input-number
                v-model="cat.number_of_players"
                :min="0"
                size="mini"
                @change="recalculate"
              />
            </base-input>
          </div>
          <div class="pricing-row__total text-white">
            {{ getAmount(getLineTotal(cat)) }}
          </div>
        </div>

        <!-- Private booking fee (tickets+fee upgrade): pre-tax component so the
             ticket lines + fee reconcile to the subtotal. -->
        <div v-if="isTicketsPlusFee && privateBookingFee > 0" class="summary-line">
          <span class="text-white">Private booking fee</span>
          <span class="text-white">{{ getAmount(privateBookingFee) }}</span>
        </div>

        <!-- Subtotal -->
        <div class="summary-line summary-line--total">
          <span class="text-white">Subtotal</span>
          <span class="text-white">{{ getAmount(subtotal) }}</span>
        </div>

        <!-- Total participants -->
        <div class="summary-line">
          <span class="text-white font-weight-bold">{{ totalParticipants }} {{ totalParticipants === 1 ? 'participant' : 'participants' }}</span>
        </div>

        <!-- Error message -->
        <div v-if="errorMessage" class="edit-error mt-3">
          <i class="tim-icons icon-alert-circle-exc mr-1"></i>
          {{ errorMessage }}
        </div>
      </template>
    </div>

    <!-- Footer -->
    <div class="view-footer">
      <base-button size="sm" type="default" @click="$emit('back')">Cancel</base-button>
      <base-button
        size="sm"
        type="primary"
        :loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        Save
      </base-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_LOCATION } from '@/api-config';
import { Auth } from 'aws-amplify';
import currencies from '@/assets/currencies.json';
import { formatCurrency } from '@/util/currencyFormat';
import { calculateSubtotal, findPriceForPlayerCount, applyPrivateUpgrade, roundCents, filterPricingByDuration } from '@/util/pricing';

const normalizeCategoryName = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');

export default {
  name: 'EditTicketsView',

  props: {
    booking: { type: Object, required: true },
    drawerData: { type: Object, required: true },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    currencySymbol: { type: String, default: '$' }
  },

  data() {
    const group = JSON.parse(localStorage.getItem('group') || '{}');

    return {
      categories: [],
      loadingCategories: false,
      saving: false,
      errorMessage: '',
      pricingTypeId: 1,
      resolvedTiers: [],
      // Fixed nominal fee for a tickets+fee private upgrade, captured at load as
      // (booking subtotal - original ticket subtotal). Stays constant while the
      // user edits ticket counts so the fee is never wiped on save.
      privateBookingFee: 0,
      currency: group.currency || 'USD',
      numberFormat: group.number_format || 0,
      currencyFormat: group.currency_format || 0,
      currencies: currencies.currencies
    };
  },

  computed: {
    cumulativePricing() {
      return !!(this.drawerData.pricing && this.drawerData.pricing.cumulative_category_pricing);
    },
    totalParticipants() {
      return this.categories.reduce((sum, c) => sum + (c.number_of_players || 0), 0);
    },
    isTicketsPlusFee() {
      return !!(this.booking && this.booking.is_reservation_upgraded
        && this.booking.reservation_upgrade_pricing_mode === 'tickets_plus_fee');
    },
    subtotal() {
      // Use shared pricing util so cumulative + per-category + per-person/flat
      // all stay in lockstep with CreateBookingView/RescheduleView.
      const result = calculateSubtotal({
        pricing: this.resolvedTiers,
        categories: this.categories,
        cumulativePricing: this.cumulativePricing,
        pricingTypeId: this.pricingTypeId
      });
      // tickets+fee upgrade: add the fixed private fee on top of the recomputed
      // per-ticket subtotal (single source of truth: applyPrivateUpgrade), so
      // editing counts never wipes the fee.
      const base = this.isTicketsPlusFee
        ? applyPrivateUpgrade({ baseSubtotal: result.subtotal, mode: 'tickets_plus_fee', fee: this.privateBookingFee })
        : result.subtotal;
      return roundCents(base).toFixed(2);
    },
    canSave() {
      return this.categories.length > 0 && !this.saving && !this.isPriceEditing;
    },
    isPriceEditing() {
      return this.categories.some(c => c.editing) || false;
    }
  },

  async mounted() {
    await this.loadTicketData();
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    async loadTicketData() {
      this.loadingCategories = true;
      try {
        const bookingSlotId = this.booking.id || this.drawerData.booking.id;

        // Use resolved pricing from drawer (handles custom vs game correctly)
        const pricing = this.drawerData.pricing || {};
        this.pricingTypeId = pricing.pricing_type_id || 1;
        this.resolvedTiers = pricing.tiers || [];

        // Existing ticket quantities for this slot (drawerData.tickets is the
        // unified list across master + children; filter by this slot).
        const allTickets = this.drawerData.tickets || [];
        const ticketsBySlot = allTickets.filter(t => Number(t.booking_slot_id) === Number(bookingSlotId));
        //Multiple durations: the booked duration is whatever this slot's tickets were sold under (their category tag);
        //a public master's own row never carries it, and a staff time edit clears the slot's tag.
        const taggedTicket = ticketsBySlot.find(t => t.game_duration_id !== undefined);
        const bookedDurationId = taggedTicket ? (taggedTicket.game_duration_id ?? null) : (pricing.selected_duration_id ?? null);
        this.resolvedTiers = filterPricingByDuration(this.resolvedTiers, bookedDurationId);
        const ticketByCategory = new Map(ticketsBySlot.map(t => [Number(t.category_id), t]));

        // Seed one row per distinct category from pricing tiers (matches
        // legacy modal — categories come from game_pricing_categories, not
        // from the tickets table). Merge in player counts and existing
        // ticket id where present.
        const categoryMap = new Map();
        for (const tier of this.resolvedTiers) {
          const catId = Number(tier.category_id);
          if (!categoryMap.has(catId)) {
            categoryMap.set(catId, {
              category_id: catId,
              category: tier.category || 'General',
              category_tiers: []
            });
          }
          categoryMap.get(catId).category_tiers.push(tier);
        }
        // A ticket whose category the live price list no longer carries (the event's
        // pricing was re-saved after this party booked, OTC-3153) keeps its own row and its
        // own tier table, the same rule as the transaction page. Without this the party
        // seeded as 0 participants at the new price and a save would have repriced it.
        const retiredTiers = (pricing.retired_tiers || []).filter(t => t.category_id !== undefined);
        for (const ticket of ticketsBySlot) {
          const catId = Number(ticket.category_id);
          if (!Number.isFinite(catId) || catId <= 0 || categoryMap.has(catId)) continue;
          // A 0-participant ticket (every save writes one per category) has no party to
          // protect: skip it so the live category seeds normally instead of a dead row
          // with no tiers that would price added players at 0.
          if (!(Number(ticket.number_of_players) > 0)) continue;
          const tiers = retiredTiers.filter(t => Number(t.category_id) === catId);
          categoryMap.set(catId, {
            category_id: catId,
            category: ticket.category || (tiers[0] && tiers[0].category) || 'General',
            category_tiers: tiers
          });
          this.resolvedTiers = this.resolvedTiers.concat(tiers);
          // The live replacement usually keeps the name ("Person" at the new price). Two same-named
          // rows invite staff to add the extra player on the wrong one, so the live row is dropped
          // when no ticket sits on it; the form then lists the same rows as the transaction page.
          const name = normalizeCategoryName(ticket.category);
          for (const [liveId, live] of categoryMap) {
            if (liveId === catId || ticketByCategory.has(liveId)) continue;
            if (normalizeCategoryName(live.category) === name) categoryMap.delete(liveId);
          }
        }

        // For cumulative pricing, the rate looked up at seed time depends on
        // total players, not per-category. Compute total once and pass it in.
        const totalPlayers = Array.from(ticketByCategory.values())
          .reduce((sum, t) => sum + (t.number_of_players || 0), 0);

        this.categories = Array.from(categoryMap.values()).map(cat => {
          const existing = ticketByCategory.get(cat.category_id);
          const players = existing ? (existing.number_of_players || 0) : 0;
          // Tier-derived rate: cumulative uses total players, per-category
          // uses this row's player count. Falls back to first tier when no
          // tier matches (e.g. zero players seeded for a new category).
          const lookupCount = this.cumulativePricing ? totalPlayers : players;
          const tierRate = parseFloat(findPriceForPlayerCount(cat.category_tiers, lookupCount)) || 0;
          // Override detection compares in TOTAL space (matches TransactionDetails:
          // tickets.price is a total in the DB; for per-person the tier total is
          // rate*players; for flat rate it's just the rate). 0-qty stub rows
          // compare 0 == 0 and don't get falsely flagged as user overrides.
          const tierTotal = this.pricingTypeId === 1 ? tierRate * players : tierRate;
          const storedTotal = existing ? parseFloat(existing.price || 0) : tierTotal;
          const isOverride = !!existing && Math.abs(storedTotal - tierTotal) > 0.01;
          // Form binds to per-unit rate (per-person) or total (flat). When
          // overridden + per-person + has players, derive rate from stored total.
          let rowPrice;
          if (isOverride && this.pricingTypeId === 1 && players > 0) {
            rowPrice = parseFloat((storedTotal / players).toFixed(2));
          } else if (isOverride) {
            rowPrice = storedTotal;
          } else {
            rowPrice = tierRate;
          }
          const row = {
            id: existing ? existing.id : null,
            category_id: cat.category_id,
            category: cat.category,
            number_of_players: players,
            price: rowPrice,
            pricing: cat.category_tiers,
            editing: false
          };
          if (isOverride) {
            row.original_price = tierRate;
          }
          return row;
        });

        // tickets+fee upgrade: capture the fixed private fee once, as the
        // booking's stored subtotal minus the original ticket prices. Editing
        // counts recomputes the ticket portion but this fee stays constant.
        if (this.isTicketsPlusFee) {
          const originalTicketSubtotal = ticketsBySlot.reduce((sum, t) => sum + parseFloat(t.price || 0), 0);
          this.privateBookingFee = roundCents(parseFloat(this.booking.price || 0) - originalTicketSubtotal);
        }
      } catch (error) {
        console.error('Failed to load ticket data:', error);
        this.categories = [];
      } finally {
        this.loadingCategories = false;
      }
    },

    getBasePrice(ticket) {
      if (this.pricingTypeId === 1) {
        if (ticket.price && ticket.number_of_players > 0) {
          return parseFloat((parseFloat(ticket.price) / ticket.number_of_players).toFixed(2));
        }
        return parseFloat(ticket.price || 0);
      }
      return parseFloat(ticket.price || 0);
    },

    getAmount(value) {
      return formatCurrency(value, {
        currency: this.currency,
        currencySymbol: this.currencySymbol,
        currencyFormat: this.currencyFormat,
        numberFormat: this.numberFormat
      });
    },

    getRate(cat) {
      const categoryName = cat.category || 'person';
      if (this.pricingTypeId === 1) {
        return `${this.getAmount(cat.price || 0)} per ${categoryName.toLowerCase()}`;
      }
      return `${this.getAmount(cat.price || 0)} flat rate`;
    },

    getLineTotal(cat) {
      if (!cat || cat.number_of_players === 0) return 0;
      if (this.pricingTypeId === 1) {
        return (cat.price || 0) * (cat.number_of_players || 0);
      }
      return cat.price || 0;
    },

    recalculate() {
      // For cumulative pricing the lookup uses TOTAL players across all
      // categories; for per-category it uses each row's count. Skip rows with
      // a user override (`original_price` is only set when actually overridden).
      // Uses findPriceForPlayerCount so display and calculateSubtotal can't
      // diverge on overlapping or unordered tiers.
      const total = this.totalParticipants;
      for (const cat of this.categories) {
        if (cat.original_price !== undefined) continue;
        if (!cat.pricing || cat.pricing.length === 0) continue;
        const lookupCount = this.cumulativePricing ? total : cat.number_of_players;
        const price = findPriceForPlayerCount(cat.pricing, lookupCount);
        if (price > 0) {
          cat.price = parseFloat(price);
        }
      }
    },

    // Price editing (matches CreateBookingView pattern)
    startEditPrice(cat) {
      // `$set` (not bare assignment) so Vue 2 makes original_price reactive.
      // Bare assignment leaves it non-observed, so the `subtotal` computed
      // keeps returning its cached pre-override value (tier × qty) and the
      // saved body.price diverges from the ticket rows.
      //
      // Snapshot the pre-edit state so the red ✕ (cancel) can return to it
      // even when the row was already overridden before this edit. Without
      // the snapshot, cancel and restore collapse to the same behavior
      // ("revert to tier") and a re-edit of a prior override silently loses
      // the prior override on cancel.
      this.$set(cat, '_preEditPrice', cat.price);
      this.$set(cat, '_preEditOverride', cat.original_price);
      if (cat.original_price === undefined) {
        this.$set(cat, 'original_price', cat.price);
      }
      this.$set(cat, 'editing', true);
      this.$forceUpdate();
    },

    saveEditPrice(cat) {
      // Commit: drop the snapshot.
      this.$delete(cat, '_preEditPrice');
      this.$delete(cat, '_preEditOverride');
      this.$set(cat, 'editing', false);
      this.$forceUpdate();
    },

    cancelEditPrice(cat) {
      // Return to the exact state captured at startEditPrice. Preserves a
      // prior override (or its absence) — does NOT unconditionally revert
      // to tier; that's what restoreOriginalPrice is for.
      if (cat._preEditPrice !== undefined) {
        cat.price = cat._preEditPrice;
      }
      if (cat._preEditOverride !== undefined) {
        this.$set(cat, 'original_price', cat._preEditOverride);
      } else {
        this.$delete(cat, 'original_price');
      }
      this.$delete(cat, '_preEditPrice');
      this.$delete(cat, '_preEditOverride');
      this.$set(cat, 'editing', false);
      this.$forceUpdate();
    },

    restoreOriginalPrice(cat) {
      // Always clear the override and use the tier price, regardless of
      // edit state. Reachable both inline (during edit) and from the
      // "overridden from $X" indicator outside edit mode.
      if (cat.original_price !== undefined) {
        cat.price = cat.original_price;
        this.$delete(cat, 'original_price');
      }
      this.$delete(cat, '_preEditPrice');
      this.$delete(cat, '_preEditOverride');
      this.$set(cat, 'editing', false);
      this.$forceUpdate();
    },

    async save() {
      if (!this.canSave) return;

      this.saving = true;
      this.errorMessage = '';

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const bookingSlotId = this.booking.id || this.drawerData.booking.id;
        const gameId = this.drawerData.booking.game_id;

        // Build tickets array matching what the backend expects
        const tickets = this.categories.map(cat => {
          const ticket = {
            category_id: cat.category_id,
            category: cat.category,
            number_of_players: cat.number_of_players,
            price: cat.price
          };
          if (cat.id) ticket.id = cat.id;
          // original_price is only present when the user has overridden the
          // tier price — backend uses this to flag custom pricing.
          if (cat.original_price !== undefined) {
            ticket.original_price = cat.original_price;
          }
          return ticket;
        });

        // Slot timing (date/time) lives on the master row only — children
        // share the same slot timing and the children mapper doesn't carry
        // those fields. Per-transaction fields (transaction_id, upgrade flag)
        // come from the active slot (`this.booking`).
        // Compute the body price fresh instead of reading `this.subtotal`.
        // The computed has historically gone stale on override; calling
        // calculateSubtotal directly applies the same per-person / flat-rate
        // and cumulative / per-category rules — including the qty=0 skip in
        // per-category flat and the totalPlayers=0 short-circuit in cumulative
        // flat — without the Vue cache in the way.
        const { subtotal: ticketsSubtotal } = calculateSubtotal({
          pricing: this.resolvedTiers,
          categories: this.categories,
          cumulativePricing: this.cumulativePricing,
          pricingTypeId: this.pricingTypeId
        });
        // tickets+fee upgrade: persist tickets + the fixed private fee, so the
        // saved booking_slots.price keeps the fee instead of dropping to the
        // bare ticket subtotal. Mirrors the subtotal computed.
        const bodyPrice = this.isTicketsPlusFee
          ? applyPrivateUpgrade({ baseSubtotal: ticketsSubtotal, mode: 'tickets_plus_fee', fee: this.privateBookingFee })
          : ticketsSubtotal;

        const b = this.booking;
        const slot = this.drawerData.booking;
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/bookings/${bookingSlotId}/v2`;
        await axios.put(url, {
          transaction: b.transaction_id,
          booking_date: String(slot.booking_date).substring(0, 10),
          end_date: String(slot.end_date || slot.booking_date).substring(0, 10),
          start_time: slot.start_time,
          end_time: slot.end_time,
          booking_copy: bookingSlotId,
          // Preserve the existing upgrade state — saving tickets must not
          // un-upgrade a private reservation.
          book_entire_slot: !!b.is_reservation_upgraded,
          tickets,
          game_id: gameId,
          group_size: this.totalParticipants,
          price: roundCents(bodyPrice)
        }, config);

        this.$emit('updated');
      } catch (error) {
        console.error('Failed to save tickets:', error);
        this.errorMessage = error.response?.data?.message || error.response?.data || 'Failed to update tickets';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.edit-tickets-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.view-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 12px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}

.view-title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.view-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

// Pricing rows (matches CreateBookingView)
.pricing-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.pricing-row__info {
  flex: 1;
  min-width: 0;
}

.pricing-row__name-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pricing-row__name {
  font-size: 13px;
  font-weight: 500;
}

.pricing-row__rate {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  display: block;
  margin-top: 2px;
}

.pricing-row__edit-icon {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  &:hover { color: #e14eca; }
}

.pricing-row__was {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  display: block;
  margin-top: 1px;
}

.pricing-row__reset-icon {
  font-size: 10px;
  color: #e14eca;
  cursor: pointer;
  margin-left: 4px;
  &:hover { color: #fff; }
}

.pricing-row__inline-edit {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.pricing-row__price-input {
  width: 90px;
}

.pricing-row__save-icon {
  color: #2dce89;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #24a46d; }
}

.pricing-row__restore-icon {
  color: #1d8cf8;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #1171d6; }
}

.pricing-row__cancel-icon {
  color: #f5365c;
  cursor: pointer;
  font-size: 12px;
  &:hover { color: #d42a4a; }
}

.pricing-row__qty {
  width: 110px;
}

.pricing-row__total {
  width: 80px;
  text-align: right;
  font-weight: 500;
  font-size: 13px;
  padding-top: 22px;
}

// Summary lines
.summary-line {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.summary-line--total {
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 600;
  font-size: 14px;
}

.edit-error {
  color: #F5365C;
  font-size: 13px;
  padding: 10px 12px;
  background: rgba(245, 54, 92, 0.1);
  border-radius: 6px;
}

.view-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  gap: 12px;
}
</style>

<style lang="scss">
.white-content {
  .edit-tickets-view {
    .view-header {
      border-bottom-color: #e9ecef;
    }

    .view-title {
      color: #344675;
    }

    .back-btn {
      background: #f0f2f5;
      border-color: #d1d5db;
      color: #525f7f;

      &:hover {
        background: #e2e6ea;
        color: #344675;
      }
    }

    .pricing-row {
      border-bottom-color: #e3e3e3;
    }

    .pricing-row__rate {
      color: #9A9A9A;
    }

    .summary-line {
      color: #525f7f;
    }

    .summary-line--total {
      border-top-color: #e3e3e3;
    }

    .view-footer {
      border-top-color: #e9ecef;
    }

    .text-white {
      color: #344675 !important;
    }
  }
}
</style>
